import type {ReactElement} from 'react';
import {cloneElement} from 'react';
import type {OverlayChildren} from 'react-bootstrap/Overlay';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {Text, TextSize} from '@Components/text';
import {CreditsModalContent} from '@Components/ai-chat-window/components/credits-modal-content/credits-modal-content';
import {openMoreOptionsModal} from '@Modals/more-options-modal/more-options-modal-library';
import {CREDITS_MODAL_PANEL_ID, MODAL_PANEL_PADDING_CLASS, MODAL_TEXT_CLASSES, TOOLTIP_TEXT_CLASSES} from '@AiChat/components/ai-chat-credits-dropdown/ai-chat-credits-dropdown.types';
import {useCreditsExpiryLines} from '@AiChat/components/ai-chat-credits-dropdown/use-credits-expiry-lines';
import type {CreditsExpiryBreakdownProps} from './credits-expiry-breakdown.types';
import styles from './credits-expiry-breakdown.module.scss';

export function CreditsExpiryBreakdown({anchor, isMobile, onOpenSummaryModal}: CreditsExpiryBreakdownProps): ReactElement | null {
  const lines = useCreditsExpiryLines();

  const getLinesList = (textClasses: string): ReactElement => {
    return (
      <ul className={styles.list}>
        {lines.map((line) => (
          <li key={line}>
            <Text val={line} size={TextSize.MD} className={textClasses} />
          </li>
        ))}
      </ul>
    );
  };

  const getTooltip = (): OverlayChildren => {
    return <Tooltip className=" _rounded-arrow">{getLinesList(TOOLTIP_TEXT_CLASSES)}</Tooltip>;
  };

  const openBreakdownModal = (): void => {
    openMoreOptionsModal({
      headerTitle: window.i18next.t('pmwjs_ai_credits_details'),
      content: (
        <CreditsModalContent>
          <div className="flex-v-row gap-2">
            <Text val={window.i18next.t('pmwjs_ai_credits_breakdown_intro')} size={TextSize.MD} className={MODAL_TEXT_CLASSES} />
            {getLinesList(MODAL_TEXT_CLASSES)}
          </div>
        </CreditsModalContent>
      ),
      panelId: CREDITS_MODAL_PANEL_ID,
      className: MODAL_PANEL_PADDING_CLASS,
      shouldCenterAlignTitle: true,
      showBackButton: true,
      showCloseButton: true,
      closeModalOnBack: false,
      onBack: onOpenSummaryModal,
    });
  };

  if (lines.length === 0) {
    return null;
  }

  if (isMobile) {
    return cloneElement(anchor, {onClick: openBreakdownModal});
  }

  return (
    <OverlayTrigger trigger={['hover', 'focus']} placement="bottom" delay={{show: 0, hide: 0}} overlay={getTooltip()}>
      {anchor}
    </OverlayTrigger>
  );
}
